import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ToastComponent } from '@shared/components/toast/toast.component';

export interface ToastConfig {
  panelClass?: string | string[];
  duration?: number;
  horizontalPosition?: 'start' | 'center' | 'end';
  verticalPosition?: 'top' | 'bottom';
}

export interface ToastDismiss {
  dismissedByAction: boolean;
}

export class ToastRef {
  private readonly action$ = new Subject<void>();
  /** ReplaySubject, damit ein Aufrufer, der erst nach dem Schließen subscribed, nicht hängen bleibt. */
  private readonly dismissed$ = new ReplaySubject<ToastDismiss>(1);
  private closed = false;

  constructor(private readonly overlayRef: OverlayRef) {}

  onAction(): Observable<void> {
    return this.action$.asObservable();
  }

  afterDismissed(): Observable<ToastDismiss> {
    return this.dismissed$.asObservable();
  }

  triggerAction(): void {
    if (this.closed) {
      return;
    }
    this.action$.next();
    this.action$.complete();
    this.close(true);
  }

  dismiss(): void {
    this.close(false);
  }

  private close(dismissedByAction: boolean): void {
    if (this.closed) {
      return;
    }
    this.closed = true;
    this.overlayRef.dispose();
    this.action$.complete();
    this.dismissed$.next({ dismissedByAction });
    this.dismissed$.complete();
  }
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private readonly overlay = inject(Overlay);
  private openRef?: ToastRef;

  open(message: string, action?: string, config: ToastConfig = {}): ToastRef {
    this.openRef?.dismiss();

    const overlayRef = this.overlay.create({
      positionStrategy: this.buildPosition(config),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
    });

    const componentRef = overlayRef.attach(new ComponentPortal(ToastComponent));
    const instance = componentRef.instance;
    instance.message = message;
    instance.action = action;

    // Wie MatSnackBar: die Klassen landen auf dem Container, nicht auf dem Overlay-Pane.
    const host: HTMLElement = componentRef.location.nativeElement;
    this.toPanelClasses(config.panelClass).forEach((cssClass) => host.classList.add(cssClass));

    const ref = new ToastRef(overlayRef);
    this.openRef = ref;
    instance.actionClick.pipe(take(1)).subscribe(() => ref.triggerAction());

    if (config.duration && config.duration > 0) {
      const timer = setTimeout(() => ref.dismiss(), config.duration);
      ref
        .afterDismissed()
        .pipe(take(1))
        .subscribe(() => clearTimeout(timer));
    }

    ref
      .afterDismissed()
      .pipe(take(1))
      .subscribe(() => {
        if (this.openRef === ref) {
          this.openRef = undefined;
        }
      });

    return ref;
  }

  private buildPosition(config: ToastConfig) {
    const position = this.overlay.position().global();

    switch (config.horizontalPosition ?? 'center') {
      case 'start':
        position.left('0');
        break;
      case 'end':
        position.right('0');
        break;
      default:
        position.centerHorizontally();
    }

    if ((config.verticalPosition ?? 'bottom') === 'top') {
      position.top('0');
    } else {
      position.bottom('0');
    }

    return position;
  }

  private toPanelClasses(panelClass?: string | string[]): string[] {
    if (!panelClass) {
      return [];
    }
    return Array.isArray(panelClass) ? panelClass : [panelClass];
  }
}
